import {
  Alert,
  Button,
  Empty,
  Flex,
  Radio,
  Result,
  Spin,
  Steps,
  Typography
} from 'antd'
import { CircleCheck } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ContentSpinner, QueryErrorAlert } from 'src/components'

import { ClientInstructions } from './ClientInstructions'
import { useMcpPolicies, useMyMcpConnections, useMyMcpServices } from './hooks'
import {
  connectableServices,
  newOauthConnection,
  serviceOptions
} from './model'
import { ServicePicker } from './ServicePicker'
import { MCP_CLIENTS, OAUTH_CLIENTS, type McpClientId } from './snippets'
import type { McpServiceId } from './types'

const { Text } = Typography

type Props = {
  workspaceId: number
  isAdmin: boolean
  /** A freshly issued PAT; the drawer owns it so closing can warn before it is lost. */
  token: string | null
  onTokenChange: (token: string | null) => void
  onDone: () => void
}

export const ConnectWizard = ({
  workspaceId,
  isAdmin,
  token,
  onTokenChange,
  onDone
}: Props) => {
  const { t } = useTranslation()
  const services = useMyMcpServices(workspaceId)
  const policies = useMcpPolicies(workspaceId, isAdmin)
  const [serviceIds, setServiceIds] = useState<McpServiceId[]>([])
  const [clientId, setClientId] = useState<McpClientId | null>(null)
  // Connections that existed when the client was picked; anything new is this client signing in.
  const [knownIds, setKnownIds] = useState<Set<string> | null>(null)

  // Kept in picker order; a service switched off meanwhile simply drops out.
  const picked = connectableServices(services.data ?? []).filter((s) =>
    serviceIds.includes(s.service)
  )
  const several = picked.length > 1
  // A personal token belongs to one service, so the token client needs exactly one.
  const client =
    picked.length && !(several && clientId === 'other') ? clientId : null
  const oauth = !!client && OAUTH_CLIENTS.has(client) && knownIds !== null
  const connections = useMyMcpConnections(workspaceId, oauth)
  const progress = oauth
    ? picked.map((s) => ({
        service: s.service,
        connection: newOauthConnection(
          connections.data ?? [],
          knownIds,
          s.service
        )
      }))
    : []
  const allConnected =
    progress.length > 0 && progress.every((row) => row.connection)
  const waiting = oauth && !allConnected

  if (services.isError || policies.isError) {
    return (
      <QueryErrorAlert
        onRetry={() => {
          void services.refetch()
          if (isAdmin) void policies.refetch()
        }}
      />
    )
  }
  if (services.isLoading || (isAdmin && policies.isLoading)) {
    return <ContentSpinner minHeight={240} />
  }

  const options = serviceOptions(
    services.data ?? [],
    isAdmin ? (policies.data ?? []) : null
  )
  if (!options.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Text type="secondary">
            {t(
              isAdmin
                ? 'workspace.aiConnectors.servicesTab.empty'
                : 'workspace.aiConnectors.connect.noServices'
            )}
          </Text>
        }
      />
    )
  }

  const pickClient = (next: McpClientId) => {
    setClientId(next)
    setKnownIds(
      connections.data ? new Set(connections.data.map((c) => c.id)) : null
    )
  }

  const current = !picked.length ? 0 : !client ? 1 : allConnected ? 3 : 2
  const finished = allConnected || !!token

  return (
    <Flex vertical gap={20}>
      <Steps
        size="small"
        current={current}
        items={[
          { title: t('workspace.aiConnectors.connect.serviceStep') },
          { title: t('workspace.aiConnectors.connect.clientStep') },
          { title: t('workspace.aiConnectors.connect.instructionsStep') }
        ]}
      />

      <Flex vertical gap={8}>
        <Text strong>{t('workspace.aiConnectors.connect.chooseService')}</Text>
        {isAdmin ? (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('workspace.aiConnectors.connect.adminServicesHint')}
          </Text>
        ) : null}
        <ServicePicker
          workspaceId={workspaceId}
          options={options}
          value={picked.map((s) => s.service)}
          disabled={!!token}
          onChange={setServiceIds}
        />
      </Flex>

      {picked.length ? (
        <Flex vertical gap={8}>
          <Text strong>{t('workspace.aiConnectors.connect.chooseClient')}</Text>
          <Radio.Group
            optionType="button"
            aria-label={t('workspace.aiConnectors.connect.chooseClient')}
            disabled={!!token}
            value={client}
            onChange={(e) => pickClient(e.target.value)}
            options={MCP_CLIENTS.map((c) => ({
              value: c,
              label: t(`workspace.aiConnectors.clients.${c}`),
              disabled: several && c === 'other'
            }))}
          />
          {several ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('workspace.aiConnectors.connect.tokenOneService')}
            </Text>
          ) : null}
        </Flex>
      ) : null}

      {token ? (
        <Alert
          type="info"
          showIcon
          title={t('workspace.aiConnectors.connect.tokenLocked')}
        />
      ) : null}

      {client && !allConnected ? (
        <ClientInstructions
          key={`${picked.map((s) => s.service).join(',')}:${client}`}
          workspaceId={workspaceId}
          services={picked}
          client={client}
          token={token}
          onTokenChange={onTokenChange}
        />
      ) : null}

      {waiting ? (
        <Alert
          type="info"
          showIcon
          icon={<Spin size="small" />}
          title={t('workspace.aiConnectors.connect.waitingTitle')}
          description={
            <Flex vertical gap={6}>
              <Text>{t('workspace.aiConnectors.connect.waitingBody')}</Text>
              {several
                ? progress.map((row) => (
                    <Flex key={row.service} align="center" gap={6}>
                      {row.connection ? (
                        <CircleCheck
                          size={14}
                          color="var(--ant-color-success)"
                        />
                      ) : (
                        <Spin size="small" />
                      )}
                      <Text>
                        {t(
                          `workspace.aiConnectors.services.${row.service}.name`
                        )}
                      </Text>
                    </Flex>
                  ))
                : null}
            </Flex>
          }
        />
      ) : null}

      {allConnected ? (
        <Result
          status="success"
          title={t('workspace.aiConnectors.connect.connectedTitle')}
          subTitle={t('workspace.aiConnectors.connect.connectedBody', {
            name: progress[0]?.connection?.name,
            services: progress
              .map((row) =>
                t(`workspace.aiConnectors.services.${row.service}.name`)
              )
              .join(', ')
          })}
        />
      ) : null}

      {client ? (
        <Flex justify="flex-end">
          <Button type={finished ? 'primary' : 'default'} onClick={onDone}>
            {t('workspace.aiConnectors.connect.done')}
          </Button>
        </Flex>
      ) : null}
    </Flex>
  )
}
